import React, { useEffect } from 'react';

import { ReportToolTip, StudentMetricsDisplay } from 'pages/reports/components/StudentMetricsDisplay/StudentMetricsDisplay';
import type { AverageMetrics } from 'pages/reports/components/StudentMetricsDisplay/StudentMetricsDisplay';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { orderBy, SortDescriptor } from 'utils/dataQuery';
import { buildExportFileName, GenerateUUID } from 'utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { ReportSection } from 'apis/ReportApi';
import { Card, Table } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { CSVLink } from 'react-csv';

import 'pages/reports/components/ChartInfoBox/styles/chart-info-box.scss';
import 'pages/reports/components/Report70/Report70.scss';

import {
    calculateHourMinFormat,
    formatDate,
    isDateWithinLast7Days,
    MAX_TABLE_HEIGHT,
    normalizeScore,
    sortableHeader
} from '../ReportFunctions';
import { createSortChangeHandler, GridSortChangeEvent } from '../sortUtils';

interface Report70Props {
    reportSections: ReportSection[];
}

export const Report70 = ({
    reportSections
}: Report70Props) => {

    const { formatMessage } = useIntl();

    // State Management
    const [state, setState] = useSuperState({
        averageMetrics: {
            activePercent: 0,
            completedPercent: 0,
            startedPercent: 0,
            studentProgress: 0,
            timeSpent: 0
        } as AverageMetrics,
        exportFileName: 'CourseSummaryReport_Student_Outcomes.csv',
        exportRows: [] as any[],
        showClassroomColumn: false,
        sort: [] as SortDescriptor[],
        tableKey: 'tableKey',
        tableRows: [] as any[]
    });

    const { averageMetrics, exportFileName, exportRows, showClassroomColumn, sort, tableKey, tableRows } = state;

    const { createToast } = useNotification();

    // Aggregate data by student
    const aggregateDataByStudent = (data: any[]) => {

        const studentMap: Record<number, any> = {};

        data.forEach((item: any) => {

            const studentId = item.StudentProfileId;
            if (!studentMap[studentId]) {

                studentMap[studentId] = {
                    Classroom: item.Classroom,
                    resourceRecordsMap: {},
                    StudentName: item.StudentName,
                    StudentProfileId: studentId
                };

            }

            // Group by resource within each student
            const resourceId = item.ResourceId;
            if (!studentMap[studentId].resourceRecordsMap[resourceId]) {

                studentMap[studentId].resourceRecordsMap[resourceId] = [];

            }
            studentMap[studentId].resourceRecordsMap[resourceId].push(item);

        });

        return Object.values(studentMap).map((student: any) => {

            // Aggregate each resource's multiple records for this student
            const resources = Object.values(student.resourceRecordsMap).map((records: any) => {

                // Sum time and attempts, take max score for each resource
                const validScores = records.map((r: any) => r.HighestScore).filter((s: any) => s !== null && s !== undefined);
                return {
                    Attempts: records.reduce((sum: number, r: any) => sum + (r.Attempts || 0), 0),
                    CourseDateCompleted: records.find((r: any) => r.CourseDateCompleted)?.CourseDateCompleted,
                    CourseLastAccessDate: records.reduce((latest: string | null, r: any) => {

                        if (!r.CourseLastAccessDate) return latest;
                        if (!latest) return r.CourseLastAccessDate;
                        return new Date(r.CourseLastAccessDate) > new Date(latest) ? r.CourseLastAccessDate : latest;

                    }, null),
                    HighestScore: validScores.length > 0 ? Math.max(...validScores) : null,
                    ResourceId: records[0].ResourceId,
                    TimeSpentInMinutes: records.reduce((sum: number, r: any) => sum + (r.TimeSpentInMinutes || 0), 0)
                };

            });

            const totalResources = resources.length;

            // Calculate progress (percentage of completed resources)
            const completedCount = resources.filter((r: any) => r.CourseDateCompleted).length;
            const progress = totalResources > 0 ? Math.round((completedCount / totalResources) * 100) : 0;

            // Calculate resources accessed (resources with attempts or last access)
            const accessedCount = resources.filter((r: any) => r.Attempts > 0
            ).length;

            // Calculate total time spent
            const totalTime = resources.reduce((sum: number, r: any) => sum + (r.TimeSpentInMinutes || 0), 0
            );

            // Calculate average score
            const scores = resources
                .filter((r: any) => r.Attempts > 0) // Only include resources with attempts
                .map((r: any) => r.HighestScore)
                .filter((score: any) => score !== null && score !== undefined && score > 0);
            const avgScore = scores.length > 0
                ? Math.floor(scores.reduce((sum: number, score: number) => sum + normalizeScore(score), 0) / scores.length)
                : null;

            // Find most recent last access
            const lastAccessDates = resources
                .map((r: any) => r.CourseLastAccessDate)
                .filter((d: any) => d);
            const lastAccess = lastAccessDates.length > 0
                ? new Date(Math.max(...lastAccessDates.map((d: any) => new Date(d).getTime())))
                : null;

            return {
                accessedCount,
                avgScore,
                Classroom: student.Classroom,
                completedCount,
                lastAccess,
                progress,
                StudentName: student.StudentName,
                StudentProfileId: student.StudentProfileId,
                totalResources,
                totalTime
            };

        });

    };

    // No filtering needed - already aggregated by student
    const filteredRows = tableRows;

    const sortedData = orderBy(filteredRows, sort);
    const displayData = sortedData.slice(0, 500);

    const calculateAverages = () => {

        const data = reportSections[0].Components[0].Results?.Data ?? [];
        const studentMap: Record<number, any[]> = {};

        data.forEach((item: any) => {

            if (!studentMap[item.StudentProfileId]) studentMap[item.StudentProfileId] = [];
            studentMap[item.StudentProfileId].push(item);

        });

        let totalProgress = 0;
        let totalTimeSpent = 0;
        let totalStarted = 0;
        let totalCompleted = 0;
        let totalActive = 0;

        const studentCount = Object.keys(studentMap).length;
        Object.values(studentMap).forEach(studentRecords => {

            const totalTimeForStudent = studentRecords.reduce((sum, r) => sum + (r.TimeSpentInMinutes || 0), 0);
            totalTimeSpent += totalTimeForStudent;

            const completed = studentRecords.some(r => r.CourseDateCompleted);
            if (completed) {

                totalProgress += 1;
                totalCompleted += 1;

            }

            const started = studentRecords.some(r => r.Attempts && r.Attempts > 0);
            if (started) totalStarted += 1;

            const active = studentRecords.some(r => r.CourseLastAccessDate && isDateWithinLast7Days(new Date(r.CourseLastAccessDate))
            );
            if (active) totalActive += 1;

        });

        setState({
            averageMetrics: {
                activePercent: (totalActive / studentCount) * 100,
                completedPercent: (totalCompleted / studentCount) * 100,
                startedPercent: (totalStarted / studentCount) * 100,
                studentProgress: (totalProgress / studentCount) * 100,
                timeSpent: totalTimeSpent / studentCount
            }
        });

    };

    const getSortableValue = (item: any, field?: string) => {

        if (!field) return null;
        if (field === 'lastAccess') {

            return item[field] ? new Date(item[field]).getTime() : null;

        }
        if (field === 'avgScore') return item[field] !== null ? item[field] : 0;
        if (field === 'progress' || field === 'totalTime' || field === 'accessedCount') {

            return item[field] || 0;

        }
        const value = item[field];
        return value;

    };

    const handleSortChange = (event: GridSortChangeEvent) => {

        createSortChangeHandler(event, sort, val => setState({ sort: val }), getSortableValue);

    };

    const buildStudentExport = () => {

        const rows = filteredRows.map((row: any) => {

            const exportRow: any = {
                [formatMessage({ id: 'average_score.3cb75673', defaultMessage: 'Average Score' })]: row.avgScore !== null ? `${row.avgScore}%` : formatMessage({ id: 'no_score.defd94f7', defaultMessage: 'No Score' }),
                [formatMessage({ id: 'last_access.6fd7ff0b', defaultMessage: 'Last Access' })]: row.lastAccess ? formatDate(row.lastAccess) : '---',
                [formatMessage({ id: 'progress.2797cee5', defaultMessage: 'Progress' })]: `${row.progress}%`,
                [formatMessage({ id: 'resources_accessed.befb0660', defaultMessage: 'Resources Accessed' })]: formatMessage({ id: 'accessed_of_total.2b21613a', defaultMessage: '{accessed} of {total}' }, { accessed: row.accessedCount, total: row.totalResources }),
                [formatMessage({ id: 'student_name.f4347b21', defaultMessage: 'Student Name' })]: row.StudentName,
                [formatMessage({ id: 'total_time.ac5ce3ce', defaultMessage: 'Total Time' })]: calculateHourMinFormat(row.totalTime)
            };
            if (showClassroomColumn) {

                exportRow[formatMessage({ id: 'classroom.127e40cb', defaultMessage: 'Classroom' })] = row.Classroom || '---';

            }
            return exportRow;

        });
        return rows;

    };

    const handleExportCSV = () => {

        const reportName = reportSections[0]?.Title || 'Student Summary Report';
        const fileName = buildExportFileName('Report', reportName, 'csv');
        setState({ exportFileName: fileName });
        const dataRows = buildStudentExport();
        setState({ exportRows: dataRows });
        setTimeout(() => {

            const csvLink = document.getElementById('csvLink');
            csvLink?.click();

        }, 0);

    };

    // Initialize data on mount
    useEffect(() => {

        const rawData = reportSections[0].Components[0].Results?.Data ?? [];

        // Check for multiple classrooms by looking at ReportFilters
        // Find the classroom filter (typically named "Classroom" or similar)
        const classroomFilter = reportSections[0]?.ReportFilters?.find((filter: any) => filter.Name?.toLowerCase().includes('classroom') || filter.DisplayName?.toLowerCase().includes('classroom')
        );

        // If the classroom filter value contains commas, multiple classrooms are selected
        const hasMultipleClassrooms = classroomFilter?.Value?.includes(',') ?? false;
        setState({ showClassroomColumn: hasMultipleClassrooms });

        const aggregatedData = aggregateDataByStudent(rawData);
        setState({ tableRows: aggregatedData });
        calculateAverages();
        setState({ tableKey: GenerateUUID() });

    }, []);

    useEffect(() => {

        setState({ tableKey: GenerateUUID() });

    }, []);

    useEffect(() => {

        if (filteredRows.length > 500) {

            createToast(
                formatMessage(
                    { id: 'showing_first_shown_records_of_total_ple.18a484b7', defaultMessage: 'Showing first {shown} records of {total}. Please refine your filters to narrow the results.' },
                    { shown: 500, total: filteredRows.length }
                ),
                'warning'
            );

        }

    }, [filteredRows.length]);

    return (
        <>
            <StudentMetricsDisplay
                averageMetrics={averageMetrics}
                calculateHourMinFormat={calculateHourMinFormat}
            />

            <Card className="mt-4">
                <CSVLink
                    className="d-none"
                    data={exportRows}
                    filename={exportFileName}
                    id="csvLink"
                    type="button"
                >
                </CSVLink>
                <div className="p-3 d-flex justify-content-end">
                    <div className="mt-4">
                        <GMetrixButton onClick={handleExportCSV} variant="primary">
                            <FormattedMessage defaultMessage="Export to CSV" id="export_to_csv.e1de1359" />
                        </GMetrixButton>
                    </div>
                </div>
                <div style={{ maxHeight: 'calc(100vh - 70px - 24px - 105px)', overflowX: 'auto', overflowY: 'auto' }}>
                    <Table bordered hover responsive className="kendo-table mb-0">
                        <thead>
                            <tr>
                                <th
                                    className="text-start"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'StudentName' }] })}
                                    style={{ cursor: 'pointer', left: 0, minWidth: 250, position: 'sticky', zIndex: 1 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'student_enrolled_in_the_course_click_nam.763c0ecf', defaultMessage: 'Student enrolled in the course. Click name to view detailed course activity for this student.' })} trigger="hover">
                                            <span className="d-flex justify-content-start" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                <FormattedMessage defaultMessage="Student Name" id="student_name.f4347b21" />
                                            </span>
                                        </ReportToolTip>,
                                        'StudentName',
                                        sort
                                    )}
                                </th>
                                {/* Classroom column - commented out per original */}
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'progress' }] })}
                                    style={{ cursor: 'pointer', minWidth: 120 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'percent_of_course_resources_this_student.3144cd99', defaultMessage: 'Percent of course resources this student has completed. Represents lifetime progress, not limited by the date window.' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Progress" id="progress.2797cee5" />
                                            </span>
                                        </ReportToolTip>,
                                        'progress',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'accessedCount' }] })}
                                    style={{ cursor: 'pointer', minWidth: 180 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'number_of_course_resources_the_student_h.025f1f7a', defaultMessage: 'Number of course resources the student has launched at least once out of the total available resources.' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Resources Accessed" id="resources_accessed.befb0660" />
                                            </span>
                                        </ReportToolTip>,
                                        'accessedCount',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'totalTime' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'total_time_the_student_has_spent_on_cour.e72a85f7', defaultMessage: 'Total time the student has spent on course resources within the selected date window.' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Total Time" id="total_time.ac5ce3ce" />
                                            </span>
                                        </ReportToolTip>,
                                        'totalTime',
                                        sort
                                    )}
                                </th>
                                {/* Average Score column - hidden until steven says so */}
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'lastAccess' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'most_recent_activity_time_for_this_cours.946cae3a', defaultMessage: 'Most recent activity time for this course.' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Last Access" id="last_access.6fd7ff0b" />
                                            </span>
                                        </ReportToolTip>,
                                        'lastAccess',
                                        sort
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayData.map((item: any, rowIndex: number) => {

                                const handleStudentClick = () => {

                                    const url = new URL(window.location.href);
                                    const studentId = item.StudentProfileId.toString();

                                    url.pathname = '/reports/68';
                                    url.searchParams.set('StudentsCSV', studentId);
                                    window.location.href = url.toString();

                                };

                                return (
                                    <tr key={`${item.StudentProfileId}-${rowIndex}`}>
                                        <td className="text-center" style={{ backgroundColor: 'white', left: 0, overflowX: 'hidden', position: 'sticky', zIndex: 1 }}>
                                            <span
                                                onClick={handleStudentClick}
                                                style={{ color: '#0066cc', cursor: 'pointer', textDecoration: 'underline' }}
                                                title={formatMessage({ id: 'view_student_details.66d3a582', defaultMessage: 'View student details' })}
                                            >
                                                {item.StudentName}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            {item.progress}
                                            %
                                        </td>
                                        <td className="text-center">
                                            {item.accessedCount}
                                            {' '}
                                            of
                                            {item.totalResources}
                                        </td>
                                        <td className="text-center">
                                            {calculateHourMinFormat(item.totalTime)}
                                        </td>
                                        <td className="text-center">
                                            {item.lastAccess ? formatDate(item.lastAccess) : '---'}
                                        </td>
                                    </tr>
                                );

                            })}
                        </tbody>
                    </Table>
                </div>

            </Card>
        </>
    );

};